<template>
  <div class="chat" :style="styles" ref="chat">
    <div class="conversation">
      <room-chat-message
        v-for="message in filteredConversation"
        :key="message.id"
        :message="message"
        :isAbsent="isParticipantAbsent"
        @initDirectMessage="initDirectMessage"
      />
    </div>

    <div class="create-message">
      <v-form ref="form" v-model="isFormValid">
        <div class="d-flex align-top mb-3">
          <v-autocomplete
            v-model="recipient"
            :items="recipientsOptions"
            :rules="[validateParticipant]"
            @change="recipientChanged"
            dense
            return-object
            label="Recipients"
            class="recipient-select dark-background"
            :disabled="!ablyChannel"
          />
          <v-tooltip right :disabled="isMobile">
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                large
                class="ml-3"
                @click="createMessage"
                v-on="on"
                :disabled="!chatReady || interviewFinished"
              >
                Send
              </v-btn>
            </template>
            <span>Press Enter + Shift to send</span>
          </v-tooltip>
        </div>
        <v-textarea
          ref="messageField"
          :label="messageFieldLabel"
          :rules="[(v) => rules.checkRequired(v, 'Message')]"
          :disabled="!chatReady"
          @keydown.shift.enter.prevent="createMessage"
          hide-details
          solo
          v-model="txt"
        />
      </v-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';

let Ably = require('ably');
import { eventBus } from '@/lib/events';
import { vueEnv } from '@/globals';
import { differenceInSeconds } from 'date-fns';
import validationRules from '@/lib/validationRules';
import analyticSender from '../../helpers/analyticSender';
import { extractAssignmentsState } from '@/components/rooms/helpers/boardAssignment';
import { isAssignmentMessage } from '@/components/rooms/helpers/assignmentNotification';

import pick from 'lodash/pick';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import capitalize from 'lodash/capitalize';

import RoomChatMessage from '@/components/rooms/components/roomChat/RoomChatMessage';

export default {
  name: 'RoomChat',
  components: {
    RoomChatMessage,
  },
  props: {
    participants: {
      type: Array,
      default: () => [],
    },
    roomInstance: {
      type: Object,
      default: () => ({}),
    },
    visible: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
  },

  data() {
    return {
      ably: null,
      ablyChannel: null,
      ablyAssignmentBoardChannel: null,
      recipient: null,
      txt: '',
      rules: validationRules,
      isFormValid: false,
    };
  },

  computed: {
    ...mapState('rooms', ['roomInfo', 'roomChat']),

    ...mapGetters('rooms', [
      'isHost',
      'interviewFinished',
      'getParticipantJoinInfo',
    ]),

    userIdentity() {
      return this.roomInfo?.identity;
    },

    chatReady() {
      return this.userIdentity && this.ablyChannel && this.roomInstance;
    },

    messageFieldLabel() {
      if (!this.userIdentity) return 'Room was not identified';
      if (!this.ablyChannel) return 'Join to Send Message';
      if (!this.roomInstance) return 'Room is missing';

      return 'New Message';
    },

    styles() {
      return { height: '300px' };
    },

    filteredConversation() {
      return this.filterConversation();
    },

    recipientsOptions() {
      return this.generateRecipientsOptions();
    },
  },

  watch: {
    roomInfo(info) {
      if (!info) return;
      this.initChat();
    },

    chatReady(ready) {
      if (!ready) return;

      this.eventuallySetScroller();
      this.extractParticipantsAssignmentInfo();
    },

    recipientsOptions() {
      this.setDefaultRecipient();
    },

    visible() {
      this.handleVisibilityChange();
    },

    roomInstance(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.createRoomPresenceMessage(true);
      }

      if (!newVal && oldVal) {
        this.createRoomPresenceMessage(false);
      }
    },
  },

  methods: {
    ...mapMutations('rooms', ['addRoomChatMessage']),

    ...mapMutations(['showInfoModal']),

    getParticipantById(identity) {
      return this.participants.find((i) => i.identity === identity);
    },

    handleVisibilityChange() {
      if (!this.visible) return;

      setTimeout(() => {
        this.scrollToChatBottom();
      });

      eventBus.trigger('sectionShown', 'chat');
    },

    setDefaultRecipient() {
      const recipientExists = this.getRecipientById(this.recipient?.value);

      if (!recipientExists) {
        this.recipient = null;
      }
    },

    validateParticipant(participant) {
      const recipient = this.getRecipientById(participant?.value);
      if (recipient) return true;
      return 'Recipient is required';
    },

    getRecipientById(identity) {
      return this.recipientsOptions.find((i) => i.value === identity);
    },

    getDefaultOption() {
      return {
        text: this.isHost ? 'All' : 'All Hosts',
        value: '',
      };
    },

    generateRecipientsOptions() {
      const defaultOption = this.getDefaultOption();

      if (!this.participants.length) {
        return [defaultOption];
      }

      const participantOptions = this.participants.map((participant) => {
        const { identity, type } = participant || {};
        return {
          text: this.getParticipantName(participant),
          value: identity,
          type,
        };
      });

      return [defaultOption, ...participantOptions];
    },

    getParticipantName(participant) {
      if (!participant) return '';

      const { name, identity } = participant;
      if (name) return name;

      const joinRecord = this.getParticipantJoinInfo(identity);
      return joinRecord?.name ?? identity;
    },

    filterConversation() {
      if (!this.chatReady) {
        return [];
      }
      const filterType = `filterMessagesFor${this.isHost ? 'Host' : 'Guest'}`;
      let conversation = this.roomChat.slice().filter(this[filterType]);
      if (this.recipient?.value) {
        conversation = conversation.filter(this.filterMessageForRecipient);
      }
      return conversation;
    },

    filterMessageForRecipient({ recipient, identity }) {
      if (identity === this.userIdentity) {
        return recipient.value === this.recipient?.value || !recipient.value;
      }

      if (identity === this.recipient.value) {
        return recipient.value === this.userIdentity || !recipient.value;
      }

      return false;
    },

    filterMessagesForHost({ recipient, identity, system }) {
      if (system && (!identity || identity === this.userIdentity)) return;

      return (
        (identity === this.userIdentity ||
          !recipient?.value ||
          recipient.value === this.userIdentity) &&
        !this.isMessageBlocked(identity)
      );
    },

    filterMessagesForGuest({ type, recipient, identity, system }) {
      if (system) return false; // no presence messages for guests
      if (identity === this.userIdentity) return true;
      if (type === 'host') {
        return !recipient?.value || recipient.value === this.userIdentity;
      }
      return false;
    },

    isParticipantAbsent(partId) {
      if (partId === this.userIdentity) return false;
      return this.recipientsOptions.every(({ value }) => value !== partId);
    },

    isMessageBlocked({ identity, blocked }) {
      if (blocked && identity !== this.userIdentity) return true;
      const participant = this.participants.find((i) => {
        return i.identity === identity;
      });
      return participant?.blocked || false;
    },

    recipientChanged() {
      this.scrollToChatBottom();
      this.focusMessageField();
    },

    initChat() {
      if (this.ably) return;

      this.ably = new Ably.Realtime(vueEnv('ably_key'));
      this.ably.connection.on('connected', () => {
        this.setupChannel();
      });
    },

    isMessageOutgoing(identity) {
      return identity === this.userIdentity;
    },

    async setupChannel() {
      this.cleanupChannel();
      await this.$nextTick();

      const { room } = this.roomInfo;
      const channelName = `[?rewind=120m]vroom:${room}`;

      this.ablyChannel = this.ably.channels.get(channelName);

      this.ablyChannel.subscribe('message', this.processReceivedMessage);

      this.ablyChannel.subscribe('presence', this.processReceivedMessage);

      this.ablyAssignmentBoardChannel = this.ably.channels.get(
        `${channelName}guest`,
        {
          params: { untilAttach: true },
        }
      );

      this.ablyAssignmentBoardChannel.subscribe(
        'actionFromAssignmentBoard',
        (update) => {
          this.handleActionFromAssignmentBoard(update.data);
        }
      );

      if (this.isHost) {
        this.ablyChannel.subscribe('participantStateChanged', (update) => {
          if (!this.isUpdateOutdated(update?.data?.timestamp)) {
            this.updateParticipantsAssignment(update.data);
          }
        });
      }
    },

    cleanupChannel() {
      const channels = ['ablyChannel', 'ablyAssignmentBoardChannel'];

      channels.forEach((key) => {
        if (this[key]) {
          this[key].unsubscribe(); // Unsubscribe all listeners
          this[key] = null;
        }
      });
    },

    isMessageAlreadyAdded(message) {
      const MIN_GAP_IN_SECONDS = 2;
      const getGapBetweenMessage = (date) => {
        return differenceInSeconds(new Date(message.date), new Date(date));
      };

      return this.roomChat.some((i) => {
        const isEqualWithoutDate = isEqual(
          omit(i, ['date', 'self']),
          omit(message, ['date', 'self'])
        );
        if (isEqualWithoutDate) {
          return getGapBetweenMessage(i.date) < MIN_GAP_IN_SECONDS;
        }

        return false;
      });
    },

    isAssignmentMessageAlreadySent(message) {
      const { txt, recipient } = message || {};
      if (!isAssignmentMessage(txt)) return false;

      return this.roomChat.some((i) => {
        return (
          isEqual(i.txt, txt) && isEqual(i.recipient.value, recipient.value)
        );
      });
    },

    isMessageValid(message) {
      if (
        !message ||
        this.isMessageBlocked(message) ||
        this.isMessageAlreadyAdded(message) ||
        this.isAssignmentMessageAlreadySent(message)
      ) {
        return false;
      }
      return true;
    },

    processReceivedMessage({ data }) {
      if (!this.isMessageValid(data)) return;

      data.self = this.isMessageOutgoing(data.identity);
      this.addRoomChatMessage(data);
      this.eventuallySetScroller();

      // make sure the last message is incoming
      const incomingMessage = this.filteredConversation
        .slice(-1)
        .find((msg) => !msg.self);

      if (incomingMessage) {
        eventBus.trigger('receivedChatMessage', incomingMessage);
        this.showNewMessageNotification(incomingMessage);
      }
    },

    // show custom notification on mobile device
    showNewMessageNotification(message) {
      if (!message || !this.isMobile || this.visible || message?.system) return;

      this.showInfoModal({
        type: 'info',
        title: message.name,
        body: message.txt,
        fadeOut: 10000,
        clicked: () => {
          eventBus.trigger('sectionShown', 'chat');
        },
      });
    },

    scrollToChatBottom() {
      const conversation = document.querySelector('.conversation');
      if (!conversation) return;

      conversation.scroll({
        behavior: 'auto',
        left: 0,
        top: 100000,
      });
    },

    eventuallySetScroller: debounce(function () {
      this.scrollToChatBottom();
    }, 300),

    prepareMessageToSend(text) {
      const meta = pick(this.roomInfo, ['identity', 'name', 'type', 'blocked']);

      return {
        txt: text || this.txt.trim(),
        date: new Date(),
        recipient: this.recipient,
        avatar: this.roomInfo.avatar,
        ...meta,
      };
    },

    createMessage() {
      if (!this.isFormValid) return;

      const message = this.prepareMessageToSend();
      this.publishMessage(message);
    },

    createRoomPresenceMessage(joined) {
      const { identity, name, type } = this.roomInfo;
      const participantName = name || identity;
      const action = joined ? 'joined' : 'left';
      const role = capitalize(type);
      const text = `${role} <strong>${participantName}</strong> ${action}`;

      const message = {
        ...this.prepareMessageToSend(text),
        system: true,
        action,
        recipient: { value: '' },
      };

      this.ablyChannel.publish('presence', message);
    },

    publishMessage(message) {
      this.trackSentEvent(message);
      this.ablyChannel.publish('message', message);
      if (!message.system) {
        this.txt = '';
      }
    },

    trackSentEvent(message) {
      analyticSender('sent_message', {
        to_type: message?.recipient?.type,
        to_identity: (message?.recipient?.value || '').replace('host_', ''),
        to_room: this.roomInfo.room,
        to_room_name: this.roomInfo.room_name,
        message: message.txt,
      });
    },

    focusMessageField() {
      const messageField = this.$refs.messageField;
      if (!messageField?.$el) return;

      let el = messageField.$el.querySelector('textarea');
      if (el) {
        el.focus();
      }
    },

    initDirectMessage(identity) {
      const recipient = this.getRecipientById(identity);
      if (recipient) {
        this.recipient = recipient;
      }

      this.focusMessageField();
    },

    isUpdateOutdated(timestamp) {
      if (!timestamp) return true;

      const now = new Date();
      const fiveMinutesInMilliseconds = 5 * 60 * 1000;
      return now - new Date(timestamp) > fiveMinutesInMilliseconds;
    },

    async extractParticipantsAssignmentInfo() {
      await this.$nextTick();

      if (!this.ablyChannel || !this.isHost) return;

      const channelHistory = await this.ablyChannel.history({
        end: Date.now(),
        limit: 1000,
      });

      const stateChangeRecords = extractAssignmentsState(channelHistory?.items);

      Object.values(stateChangeRecords).forEach(({ participants }) => {
        participants.filter(this.isParticipantNeedUpdate).forEach((update) => {
          eventBus.trigger('participantInfoUpdated', {
            info: {
              ...update,
            },
            options: { to: 'host' },
          });
        });
      });
    },

    isParticipantNeedUpdate(update) {
      const participant = this.getParticipantById(update?.identity);

      if (!participant) return false;

      return Object.keys(update).some((key) => {
        return participant[key] !== update[key];
      });
    },

    // update assignment status and order
    updateParticipantsAssignment(update) {
      const { from, participants } = update || {};

      participants.filter(this.isParticipantNeedUpdate).forEach((update) => {
        eventBus.trigger('participantInfoUpdated', {
          info: {
            ...update,
            from,
          },
          options: { to: 'host' },
        });
      });
    },

    handleActionFromAssignmentBoard(data) {
      if (data?.to?.identity !== this.userIdentity) return;

      if (data.stopTracks) {
        eventBus.trigger('stopTracks');
        return;
      }

      if (data.action) {
        eventBus.trigger('participantAction', data.action);
        return;
      }

      if (data.info) {
        eventBus.trigger('participantInfoUpdated', data);
      }
    },
  },

  mounted() {
    this.recipient = this.getDefaultOption();
  },

  created() {
    eventBus.on('sendParticipantMessage', this.initDirectMessage);
    eventBus.on('sendAssignmentChange', this.publishMessage);
  },
};
</script>

<style lang="less" src="@/assets/styles/rooms/rooms-chat.less"></style>
